const SCANNER_STORAGE_PREFIX = "abos.hardware.scanner.v1";
const PRINTER_STORAGE_PREFIX = "abos.hardware.printer.v1";
export const HARDWARE_READINESS_EVENT = "abos:hardware-readiness-changed";

function scopeKey(prefix, workspacePublicId, storePublicId) {
  if (!workspacePublicId || !storePublicId) return "";
  return `${prefix}:${workspacePublicId}:${storePublicId}`;
}

function safeParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function emitChanged(workspacePublicId, storePublicId, hardware) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(HARDWARE_READINESS_EVENT, {
      detail: {
        workspace_public_id: workspacePublicId,
        store_public_id: storePublicId,
        hardware,
      },
    }));
  } catch {
    // Device-local readiness is convenience state only.
  }
}

function normalizePaperWidth(value) {
  return Number(value) === 58 ? 58 : 80;
}

export function getScannerReadiness(workspacePublicId, storePublicId) {
  const key = scopeKey(SCANNER_STORAGE_PREFIX, workspacePublicId, storePublicId);
  if (!key || typeof window === "undefined") return null;

  try {
    const parsed = safeParse(window.localStorage.getItem(key));
    if (!parsed || parsed.version !== 1 || !parsed.verified_at) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setScannerReadiness(workspacePublicId, storePublicId, details = {}) {
  const key = scopeKey(SCANNER_STORAGE_PREFIX, workspacePublicId, storePublicId);
  if (!key || typeof window === "undefined") return null;

  const record = {
    version: 1,
    verified_at: new Date().toISOString(),
    matched_product: Boolean(details.matched_product),
    barcode: String(details.barcode || "").slice(0, 128),
    product_public_id: details.product_public_id || null,
    product_name: details.product_name || null,
    sku: details.sku || null,
  };

  try {
    window.localStorage.setItem(key, JSON.stringify(record));
    emitChanged(workspacePublicId, storePublicId, "scanner");
  } catch {
    // Scanner readiness is device-local convenience state. POS operation
    // must never depend on localStorage being available.
  }

  return record;
}

export function clearScannerReadiness(workspacePublicId, storePublicId) {
  const key = scopeKey(SCANNER_STORAGE_PREFIX, workspacePublicId, storePublicId);
  if (!key || typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(key);
    emitChanged(workspacePublicId, storePublicId, "scanner");
  } catch {
    // Best effort only.
  }
}

export function getPrinterSettings(workspacePublicId, storePublicId) {
  const key = scopeKey(PRINTER_STORAGE_PREFIX, workspacePublicId, storePublicId);
  const fallback = {
    version: 1,
    paper_width_mm: 80,
    verified_at: null,
  };

  if (!key || typeof window === "undefined") return fallback;

  try {
    const parsed = safeParse(window.localStorage.getItem(key));
    if (!parsed || parsed.version !== 1) return fallback;
    return {
      ...fallback,
      ...parsed,
      paper_width_mm: normalizePaperWidth(parsed.paper_width_mm),
      verified_at: parsed.verified_at || null,
    };
  } catch {
    return fallback;
  }
}

export function getPrinterReadiness(workspacePublicId, storePublicId) {
  const settings = getPrinterSettings(workspacePublicId, storePublicId);
  return settings.verified_at ? settings : null;
}

export function getPrinterPaperWidth(workspacePublicId, storePublicId) {
  return getPrinterSettings(workspacePublicId, storePublicId).paper_width_mm;
}

export function setPrinterPaperWidth(workspacePublicId, storePublicId, paperWidthMm) {
  const key = scopeKey(PRINTER_STORAGE_PREFIX, workspacePublicId, storePublicId);
  if (!key || typeof window === "undefined") return null;

  const current = getPrinterSettings(workspacePublicId, storePublicId);
  const nextWidth = normalizePaperWidth(paperWidthMm);
  const record = {
    version: 1,
    paper_width_mm: nextWidth,
    // A different paper profile needs a new physical verification.
    verified_at: current.paper_width_mm === nextWidth ? current.verified_at : null,
  };

  try {
    window.localStorage.setItem(key, JSON.stringify(record));
    emitChanged(workspacePublicId, storePublicId, "printer");
  } catch {
    // Best effort only.
  }

  return record;
}

export function setPrinterReadiness(workspacePublicId, storePublicId, details = {}) {
  const key = scopeKey(PRINTER_STORAGE_PREFIX, workspacePublicId, storePublicId);
  if (!key || typeof window === "undefined") return null;

  const record = {
    version: 1,
    paper_width_mm: normalizePaperWidth(details.paper_width_mm),
    verified_at: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(key, JSON.stringify(record));
    emitChanged(workspacePublicId, storePublicId, "printer");
  } catch {
    // Printer readiness never controls server-side sale correctness.
  }

  return record;
}

export function clearPrinterReadiness(workspacePublicId, storePublicId) {
  const key = scopeKey(PRINTER_STORAGE_PREFIX, workspacePublicId, storePublicId);
  if (!key || typeof window === "undefined") return;

  const current = getPrinterSettings(workspacePublicId, storePublicId);
  const record = {
    version: 1,
    paper_width_mm: current.paper_width_mm,
    verified_at: null,
  };

  try {
    window.localStorage.setItem(key, JSON.stringify(record));
    emitChanged(workspacePublicId, storePublicId, "printer");
  } catch {
    // Best effort only.
  }
}
